import axiosInstance from "../utils/axiosConfig";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import {
  IDeviceLog,
  IDeviceLogDistrict,
  IDeviceLogEntry,
  IDeviceLogFilters,
} from "../interface/DeviceLog";

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof AxiosError)
    return error.response?.data?.message || fallback;
  if (error instanceof Error) return error.message;
  return fallback;
};

// All device logs, newest first, with optional search / district / type filters
export const fetchDeviceLogs = async (
  filters: IDeviceLogFilters,
  currentPage: number,
  itemsPerPage: number,
): Promise<{ data: IDeviceLog[]; totalItems: number }> => {
  try {
    const response = await axiosInstance.get("/LoginHistory/device-logs", {
      params: {
        search: filters.search || undefined,
        distNumber: filters.distNumber || undefined,
        siteType: filters.siteType || undefined,
        fromDate: filters.fromDate || undefined,
        toDate: filters.toDate || undefined,
        page: currentPage,
        pageSize: itemsPerPage,
      },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to fetch device logs");
    }

    return {
      data: response.data.data || [],
      totalItems: response.data.totalItems || 0,
    };
  } catch (error) {
    const errorMessage = getErrorMessage(
      error,
      "An unexpected error occurred while fetching device logs.",
    );
    toast.error(errorMessage);
    throw new Error(errorMessage);
  }
};

// Every login of one person from one device, newest first, paged.
// Pass the list's date range so the popup matches the row's count.
// No toast here: the popup shows the error itself.
export const fetchDeviceLogEntries = async (
  employeePk: number,
  deviceUuid: string | null,
  fromDate: string,
  toDate: string,
  currentPage: number,
  itemsPerPage: number,
  signal?: AbortSignal,
): Promise<{ data: IDeviceLogEntry[]; totalItems: number }> => {
  try {
    const response = await axiosInstance.get(
      "/LoginHistory/device-logs/entries",
      {
        params: {
          employeePk,
          deviceUuid: deviceUuid || undefined,
          fromDate,
          toDate,
          page: currentPage,
          pageSize: itemsPerPage,
        },
        signal,
      },
    );

    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to fetch logins");
    }

    return {
      data: response.data.data || [],
      totalItems: response.data.totalItems || 0,
    };
  } catch (error) {
    throw new Error(
      getErrorMessage(error, "An unexpected error occurred while fetching logins."),
    );
  }
};

// Dashboard card: unique devices that logged in today.
// No toast here: the dashboard shows its own message.
export const fetchTodayDeviceCount = async (): Promise<number> => {
  const response = await axiosInstance.get("/LoginHistory/device-logs/today-count");

  if (!response.data.success) {
    throw new Error(response.data.message || "Failed to fetch device count");
  }

  return Number(response.data.data) || 0;
};

// Districts for the filter dropdown
export const fetchDeviceLogDistricts = async (): Promise<
  IDeviceLogDistrict[]
> => {
  try {
    const response = await axiosInstance.get(
      "/LoginHistory/device-logs/districts",
    );

    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to fetch districts");
    }

    return response.data.data || [];
  } catch (error) {
    const errorMessage = getErrorMessage(
      error,
      "An unexpected error occurred while fetching districts.",
    );
    toast.error(errorMessage);
    throw new Error(errorMessage);
  }
};
